import { chain, concat, get } from 'lodash';
import applyTweak from '@/utilities/applyTweak';
function isSuperiorityClass(myClass) {
    return myClass.name === 'Fighter' ||
        myClass.name === 'Scholar' ||
        !!(myClass.archetype && myClass.archetype.name === 'Deadeye Technique');
}
function getClassField(classData, level, field) {
    return get(classData, ['levelChanges', level, field]) || '0';
}
function getArchetypeField(archetypeData, level, index) {
    return get(archetypeData, `leveledTable.${level}.${index}.value`) || '0';
}
function getMulticlassDice(rawCharacter, myClasses, myArchetypes, initialClass) {
    return chain(rawCharacter.classes)
        .filter(multiclassClass => isSuperiorityClass(multiclassClass) &&
        multiclassClass.name !== initialClass)
        .reduce((sum, multiclassClass) => {
        const classData = myClasses.find(({ name }) => name === multiclassClass.name);
        const classGains = (classData ? chain(classData.levelChanges)
            .map('Superiority Dice')
            .slice(0, multiclassClass.levels)
            .filter((level) => parseInt(level) > 0)
            .uniq()
            .value()
            .length : 0);
        const archetypeData = myArchetypes.find(({ name }) => name === get(multiclassClass, 'archetype.name'));
        const archetypeGains = (archetypeData ? chain(archetypeData.leveledTable)
            .pickBy((fields, level) => parseInt(level) <= multiclassClass.levels)
            .map(fields => fields ? fields[1].value : 0)
            .uniq()
            .value()
            .length : 0);
        return sum + classGains + archetypeGains;
    }, 0)
        .value();
}
function getSuperiorityValues(rawCharacter, myClasses, myArchetypes) {
    const completeClass = rawCharacter.classes.find(isSuperiorityClass);
    if (!completeClass)
        return false;
    const classData = myClasses.find(({ name }) => name === completeClass.name);
    if (!classData)
        return false;
    const archetypeData = myArchetypes.find(({ name }) => name === get(completeClass, 'archetype.name'));
    const multiclassDice = getMulticlassDice(rawCharacter, myClasses, myArchetypes, completeClass.name);
    switch (completeClass.name) {
        case 'Fighter':
            const maxDice = parseInt(getClassField(classData, completeClass.levels, 'Superiority Dice')) + multiclassDice;
            const diceSize = getClassField(classData, completeClass.levels, 'Martial Superiority');
            const saveDcAbilities = ['Strength', 'Dexterity'];
            const isTactical = completeClass.archetype && completeClass.archetype.name === 'Tactical Specialist';
            if (isTactical && archetypeData) {
                return {
                    diceSize,
                    maxDice: maxDice + parseInt(getArchetypeField(archetypeData, completeClass.levels, 0)),
                    saveDcAbilities
                };
            }
            else if (completeClass.levels === 1) {
                return false;
            }
            else {
                return {
                    diceSize,
                    maxDice,
                    saveDcAbilities
                };
            }
        case 'Scholar':
            return {
                diceSize: getClassField(classData, completeClass.levels, 'Academic Superiority'),
                maxDice: parseInt(getClassField(classData, completeClass.levels, 'Superiority Dice')) + multiclassDice,
                saveDcAbilities: ['Intelligence']
            };
        case 'Scout':
            return archetypeData ? {
                diceSize: getArchetypeField(archetypeData, completeClass.levels, 0),
                maxDice: parseInt(getArchetypeField(archetypeData, completeClass.levels, 1)) + multiclassDice,
                saveDcAbilities: ['Dexterity']
            } : false;
        default:
            return false;
    }
}
function getSaveDC(rawCharacter, proficiencyBonus, abilityScores, saveDcAbilities) {
    const maxModifier = Math.max(...saveDcAbilities.map(saveDcAbility => abilityScores[saveDcAbility].modifier));
    return applyTweak(rawCharacter, 'superiority.maneuverSaveDC', 8 + proficiencyBonus + maxModifier);
}
function getManeuvers(rawCharacter, maneuvers) {
    return chain(rawCharacter.classes)
        .map((myClass) => concat(myClass.maneuvers || [], (myClass.archetype && myClass.archetype.maneuvers) || []).map(maneuver => {
        const maneuverData = maneuvers.find(({ name }) => name === maneuver);
        if (!maneuverData)
            console.error('Warning: Maneuver not found: ' + maneuver);
        return maneuverData;
    }))
        .flatten()
        .compact()
        .value();
}
export default function generateSuperiority(rawCharacter, myClasses, myArchetypes, abilityScores, proficiencyBonus, maneuvers) {
    const superiority = getSuperiorityValues(rawCharacter, myClasses, myArchetypes);
    return superiority && {
        currentDice: rawCharacter.currentStats.superiorityDiceUsed,
        maxDice: applyTweak(rawCharacter, 'superiority.maxDice', superiority.maxDice),
        diceSize: superiority.diceSize,
        maneuverSaveDC: getSaveDC(rawCharacter, proficiencyBonus, abilityScores, superiority.saveDcAbilities),
        maneuvers: getManeuvers(rawCharacter, maneuvers)
    };
}
